import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button, Alert,
  MenuItem, IconButton, Table, TableHead, TableBody, TableRow,
  TableCell, CircularProgress, Divider, Chip, Tooltip
} from '@mui/material';
import { Add, Delete, ArrowBack, HomeWork, RequestQuote } from '@mui/icons-material';
import axios from 'axios';
import AttachmentsPanel from './AttachmentsPanel';
import { useSuppliers } from '../hooks/useSuppliers';
import { fmt, sym } from '../utils/currency';
import { useItems } from '../hooks/useItems';
import { isRealDate } from '../utils/dateValidation';
import { useGLAccounts } from '../hooks/useGLAccounts';
import { glAccountOptions } from '../utils/glAccountOptions';
import { useLocations } from '../hooks/useLocations';
import { useVATGroups } from '../hooks/useVATGroups';

const API = process.env.REACT_APP_API_URL;
const LINE_TYPES = ['Item', 'Service', 'GL Account', 'Fixed Asset'];
const DRAFT_KEY = 'pq-draft';

const emptyLine = () => ({
  _key: Date.now() + Math.random(),
  lineType: 'Service',
  itemNo: '',
  faNo: '',
  glAccount: '',
  description: '',
  qty: 1,
  unitPrice: '',
  amount: 0,
  vatGroupCode: ''
});

const PurchaseQuoteForm = () => {
  const navigate = useNavigate();
  const { quoteNo } = useParams();
  const isEdit = Boolean(quoteNo);
  const [searchParams] = useSearchParams();

  const { suppliers }            = useSuppliers();
  const { items: allItems }      = useItems();
  const { accounts: glAccounts } = useGLAccounts();
  const { locations }            = useLocations();
  const { vatGroups }            = useVATGroups();

  const [fixedAssets, setFixedAssets]     = useState([]);
  const [postingGroups, setPostingGroups] = useState([]);

  const [supplierCode,     setSupplierCode]     = useState('');
  const [quoteDate,        setQuoteDate]        = useState(new Date().toISOString().split('T')[0]);
  const [expiryDate,       setExpiryDate]       = useState('');
  const [supplierQuoteRef, setSupplierQuoteRef] = useState('');
  const [locationCode,     setLocationCode]     = useState('');
  const [lines,            setLines]            = useState([emptyLine()]);
  const [error,            setError]            = useState('');
  const [loading,          setLoading]          = useState(isEdit);
  const [saving,           setSaving]           = useState(false);
  const [status,           setStatus]           = useState('Draft');
  const [draftRestored,    setDraftRestored]    = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/fixed-assets`,                { headers }),
      axios.get(`${API}/api/fixed-assets/posting-groups`, { headers }),
    ]).then(([faRes, pgRes]) => {
      setFixedAssets(faRes.data);
      setPostingGroups(pgRes.data);
    }).catch(() => {}); // non-fatal
  }, []); // eslint-disable-line

  // Restore an unsaved draft (new quotes only).
  useEffect(() => {
    if (isEdit) return;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d.supplierCode)     setSupplierCode(d.supplierCode);
      if (d.quoteDate)        setQuoteDate(d.quoteDate);
      if (d.expiryDate)       setExpiryDate(d.expiryDate);
      if (d.supplierQuoteRef) setSupplierQuoteRef(d.supplierQuoteRef);
      if (d.locationCode)     setLocationCode(d.locationCode);
      if (Array.isArray(d.lines) && d.lines.length)
        setLines(d.lines.map(l => ({ ...l, _key: Date.now() + Math.random() })));
      setDraftRestored(true);
    } catch { localStorage.removeItem(DRAFT_KEY); }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (isEdit) return;
    const t = setTimeout(() => {
      const hasData = supplierCode ||
        lines.some(l => l.itemNo || l.faNo || l.glAccount || l.description || l.unitPrice);
      if (!hasData) return;
      localStorage.setItem(DRAFT_KEY, JSON.stringify({
        supplierCode, quoteDate, expiryDate, supplierQuoteRef, locationCode,
        lines: lines.map(({ _key, ...rest }) => rest)
      }));
    }, 500);
    return () => clearTimeout(t);
  }, [supplierCode, quoteDate, expiryDate, supplierQuoteRef, locationCode, lines, isEdit]);

  useEffect(() => {
    if (!isEdit) return;
    axios.get(`${API}/api/payables/quotes/${quoteNo}`, { headers })
      .then(({ data }) => {
        setSupplierCode(data.supplierCode);
        setQuoteDate(data.quoteDate?.split('T')[0] || '');
        setExpiryDate(data.expiryDate?.split('T')[0] || '');
        setSupplierQuoteRef(data.supplierQuoteRef || '');
        setLocationCode(data.locationCode || '');
        setLines(data.lines.map(l => ({ ...l, _key: Math.random(), faNo: l.faNo || '' })));
        setStatus(data.status || 'Draft');
      })
      .catch(err => setError(err.response?.data?.error || 'Failed to load quote'))
      .finally(() => setLoading(false));
  }, [quoteNo]); // eslint-disable-line

  // supplier-preselect: open prefilled from the vendor card (new quotes only)
  useEffect(() => { const sc = searchParams.get('supplier'); if (sc && !isEdit) setSupplierCode(sc); }, [searchParams, isEdit]);

  const itemsOfType = (type) => allItems.filter(i => i.itemType === type);
  const hasItemLines = lines.some(l => l.lineType === 'Item');

  const getAcqAccount = (faNo) => {
    const fa = fixedAssets.find(a => a.faNo === faNo);
    if (!fa) return '';
    const pg = postingGroups.find(p => p.groupCode === fa.faPostingGroup);
    return pg?.acquisitionCostAccount || '';
  };

  const updateLine = (key, field, value) => {
    setLines(prev => prev.map(l => {
      if (l._key !== key) return l;
      const updated = { ...l, [field]: value };

      if (field === 'lineType') {
        updated.itemNo      = '';
        updated.faNo        = '';
        updated.glAccount   = '';
        updated.description = '';
        updated.unitPrice   = '';
        if (value === 'Fixed Asset') updated.qty = 1;
      }
      if (field === 'itemNo' && value) {
        const item = allItems.find(i => i.itemNo === value);
        if (item) {
          updated.glAccount   = item.glAccount;
          updated.description = item.itemName;
          updated.unitPrice   = item.unitPrice;
        }
      }
      if (field === 'faNo' && value) {
        const fa = fixedAssets.find(a => a.faNo === value);
        if (fa) {
          updated.glAccount   = getAcqAccount(value);
          updated.description = fa.faName;
          updated.qty         = 1;
        }
      }
      const qty   = parseFloat(field === 'qty'       ? value : updated.qty)       || 0;
      const price = parseFloat(field === 'unitPrice' ? value : updated.unitPrice) || 0;
      updated.amount = parseFloat((qty * price).toFixed(2));
      return updated;
    }));
  };

  const addLine    = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (key) => setLines(prev => prev.filter(l => l._key !== key));

  const vatPct  = (code) => vatGroups.find(g => g.code === code)?.vatPercent || 0;
  const lineVAT = (l) => parseFloat(((l.amount || 0) * vatPct(l.vatGroupCode) / 100).toFixed(2));

  const subtotal    = lines.reduce((s, l) => s + (l.amount || 0), 0);
  const totalVAT    = lines.reduce((s, l) => s + lineVAT(l), 0);
  const totalAmount = subtotal + totalVAT;

  const handleSubmit = async () => {
    if (!supplierCode) { setError('Supplier is required'); return; }
    for (const l of lines) {
      if (l.lineType === 'Fixed Asset') {
        if (!l.faNo)      { setError('Select a Fixed Asset on each FA line'); return; }
        if (!l.glAccount) { setError(`FA ${l.faNo}: posting group has no Acquisition Cost Account configured`); return; }
      } else if (!l.glAccount) { setError('Each line must have a GL Account'); return; }
      if (!l.description.trim()) { setError('Each line must have a description'); return; }
      if (!l.qty || l.qty <= 0)  { setError('Qty must be greater than zero'); return; }
      if (!l.unitPrice || parseFloat(l.unitPrice) <= 0) { setError('Unit price must be greater than zero'); return; }
    }

    setSaving(true);
    setError('');
    try {
      const payload = {
        supplierCode, quoteDate, expiryDate, supplierQuoteRef, locationCode,
        lines: lines.map((l, i) => ({
          lineNo:      i + 1,
          lineType:    l.lineType,
          itemNo:      l.itemNo  || '',
          faNo:        l.faNo    || '',
          glAccount:   l.glAccount,
          description: l.description,
          qty:         parseFloat(l.qty),
          unitPrice:   parseFloat(l.unitPrice),
          amount:      l.amount,
          vatGroupCode: l.vatGroupCode || ''
        }))
      };
      if (isEdit) {
        await axios.put(`${API}/api/payables/quotes/${quoteNo}`, payload, { headers });
      } else {
        await axios.post(`${API}/api/payables/quotes`, payload, { headers });
        localStorage.removeItem(DRAFT_KEY);
      }
      navigate('/payables/quotes');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save quote');
    } finally {
      setSaving(false);
    }
  };

  const isReadOnly = isEdit && !['Draft', 'Sent'].includes(status);

  if (loading) return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={3} gap={1} flexWrap="wrap">
            <IconButton onClick={() => navigate('/payables/quotes')} sx={{ mr: 1 }}>
              <ArrowBack />
            </IconButton>
            <RequestQuote sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h5" color="primary">
              {isEdit ? `Edit Quote: ${quoteNo}` : 'New Purchase Quote'}
            </Typography>
            <Box ml="auto" display="flex" gap={1}>
              {!isReadOnly && (
                <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                  {saving ? 'Saving…' : isEdit ? 'Update Quote' : 'Create Quote'}
                </Button>
              )}
              <Button variant="outlined" onClick={() => navigate('/payables/quotes')}>
                {isReadOnly ? 'Back' : 'Cancel'}
              </Button>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {draftRestored && (
            <Alert severity="info" sx={{ mb: 2 }}
              onClose={() => { localStorage.removeItem(DRAFT_KEY); window.location.reload(); }}>
              Restored your unsaved draft. Close this alert to discard it and start fresh.
            </Alert>
          )}
          {isReadOnly && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This quote is <strong>{status}</strong> and cannot be edited.
            </Alert>
          )}
          {isEdit && (
            <Box mb={3}>
              <AttachmentsPanel refType="PurchaseQuote" refId={quoteNo} />
            </Box>
          )}

          {/* Header fields */}
          <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
            <TextField select label="Supplier" value={supplierCode}
              onChange={e => setSupplierCode(e.target.value)}
              disabled={isReadOnly}
              sx={{ minWidth: 280 }} size="small">
              <MenuItem value=""><em>Select Supplier</em></MenuItem>
              {suppliers.map(s => (
                <MenuItem key={s.supplierCode} value={s.supplierCode}>
                  {s.supplierCode} – {s.supplierName}
                </MenuItem>
              ))}
            </TextField>
            <TextField label="Quote Date" type="date" value={quoteDate}
              onChange={e => setQuoteDate(e.target.value)}
              disabled={isReadOnly}
              size="small" InputLabelProps={{ shrink: true }}
              error={!!(quoteDate && !isRealDate(quoteDate))}
              helperText={quoteDate && !isRealDate(quoteDate) ? 'Not a valid date' : ''} />
            <TextField label="Valid Until" type="date" value={expiryDate}
              onChange={e => setExpiryDate(e.target.value)}
              disabled={isReadOnly}
              size="small" InputLabelProps={{ shrink: true }}
              inputProps={{ min: quoteDate || undefined }}
              error={!!((expiryDate && !isRealDate(expiryDate)) || (quoteDate && expiryDate && expiryDate < quoteDate))}
              helperText={(expiryDate && !isRealDate(expiryDate)) ? 'Not a valid date' : (quoteDate && expiryDate && expiryDate < quoteDate) ? 'Before quote date' : ''} />
            <TextField label="Vendor Quote Ref" value={supplierQuoteRef}
              onChange={e => setSupplierQuoteRef(e.target.value)}
              disabled={isReadOnly}
              size="small" sx={{ minWidth: 180 }}
              helperText="Vendor's own quotation no." />
            <TextField select label="Receive-to Location" value={locationCode}
              onChange={e => setLocationCode(e.target.value)}
              disabled={isReadOnly}
              sx={{ minWidth: 220 }} size="small"
              helperText={hasItemLines ? 'Carried to the PO' : 'Optional'}>
              <MenuItem value=""><em>Select Location</em></MenuItem>
              {locations.map(loc => (
                <MenuItem key={loc.locationCode} value={loc.locationCode}>
                  {loc.locationCode} – {loc.locationName}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle1" fontWeight={600} mb={1}>Quote Lines</Typography>

          <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 120 }}><strong>Type</strong></TableCell>
                <TableCell sx={{ minWidth: 190 }}><strong>Item / FA / GL</strong></TableCell>
                <TableCell sx={{ minWidth: 170 }}><strong>Description</strong></TableCell>
                <TableCell align="center" sx={{ width: 70 }}><strong>Qty</strong></TableCell>
                <TableCell align="right" sx={{ minWidth: 110 }}><strong>Unit Price</strong></TableCell>
                <TableCell sx={{ minWidth: 120 }}><strong>VAT</strong></TableCell>
                <TableCell align="right" sx={{ minWidth: 100 }}><strong>Amount</strong></TableCell>
                <TableCell sx={{ width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map(l => (
                <TableRow key={l._key}>
                  <TableCell>
                    <TextField select value={l.lineType} size="small" fullWidth
                      disabled={isReadOnly}
                      onChange={e => updateLine(l._key, 'lineType', e.target.value)}>
                      {LINE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    {l.lineType === 'Fixed Asset' ? (
                      <TextField select value={l.faNo} size="small" fullWidth
                        disabled={isReadOnly}
                        onChange={e => updateLine(l._key, 'faNo', e.target.value)}>
                        <MenuItem value=""><em>Select Fixed Asset</em></MenuItem>
                        {fixedAssets.map(fa => (
                          <MenuItem key={fa.faNo} value={fa.faNo}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <HomeWork fontSize="small" sx={{ color: 'text.secondary' }} />
                              {fa.faNo} — {fa.faName}
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : l.lineType === 'GL Account' ? (
                      <TextField select value={l.glAccount} size="small" fullWidth
                        disabled={isReadOnly}
                        onChange={e => updateLine(l._key, 'glAccount', e.target.value)}>
                        {glAccountOptions(glAccounts)}
                      </TextField>
                    ) : (
                      <TextField select value={l.itemNo} size="small" fullWidth
                        disabled={isReadOnly}
                        onChange={e => updateLine(l._key, 'itemNo', e.target.value)}>
                        <MenuItem value=""><em>Select {l.lineType}</em></MenuItem>
                        {itemsOfType(l.lineType).map(i => (
                          <MenuItem key={i.itemNo} value={i.itemNo}>
                            {i.itemNo} – {i.itemName}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                    {l.lineType === 'Fixed Asset' && l.glAccount && (
                      <Tooltip title="Acquisition Cost Account from FA Posting Group">
                        <Chip label={l.glAccount} size="small" color="primary" variant="outlined"
                          sx={{ mt: 0.5, fontSize: '0.65rem' }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <TextField value={l.description} size="small" fullWidth
                      disabled={isReadOnly}
                      onChange={e => updateLine(l._key, 'description', e.target.value)} />
                  </TableCell>
                  <TableCell align="center">
                    <TextField value={l.qty} size="small" type="number"
                      inputProps={{ min: 0, step: 1, style: { textAlign: 'center' } }}
                      onChange={e => updateLine(l._key, 'qty', e.target.value)}
                      disabled={isReadOnly || l.lineType === 'Fixed Asset'} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField value={l.unitPrice} size="small" type="number"
                      inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                      disabled={isReadOnly}
                      onChange={e => updateLine(l._key, 'unitPrice', e.target.value)}
                      label={l.lineType === 'Fixed Asset' ? 'Acq. Cost' : undefined} />
                  </TableCell>
                  <TableCell>
                    <TextField select value={l.vatGroupCode || ''} size="small" fullWidth
                      disabled={isReadOnly}
                      onChange={e => updateLine(l._key, 'vatGroupCode', e.target.value)}>
                      <MenuItem value=""><em>No VAT</em></MenuItem>
                      {vatGroups.map(g => (
                        <MenuItem key={g.code} value={g.code}>
                          {g.code} ({g.vatPercent}%)
                        </MenuItem>
                      ))}
                    </TextField>
                    {l.vatGroupCode && lineVAT(l) > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        VAT {sym()} {fmt(lineVAT(l))}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {sym()} {fmt(l.amount)}
                  </TableCell>
                  <TableCell>
                    {!isReadOnly && (
                      <IconButton size="small" color="error" onClick={() => removeLine(l._key)}
                        disabled={lines.length === 1}>
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mt={2}>
            {!isReadOnly && (
              <Button startIcon={<Add />} onClick={addLine} variant="outlined" size="small">
                Add Line
              </Button>
            )}
            <Box ml="auto" textAlign="right">
              <Typography variant="body2" color="text.secondary">
                Subtotal: {sym()} {fmt(subtotal)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                VAT: {sym()} {fmt(totalVAT)}
              </Typography>
              <Typography variant="h6" fontWeight={700}>
                Total: {sym()} {fmt(totalAmount)}
              </Typography>
            </Box>
          </Box>

        </CardContent>
      </Card>
    </Box>
  );
};

export default PurchaseQuoteForm;
